import React, { useEffect, useState } from "react";
import { mapTableRows } from "../../../../../utils";
import { Backdrop, Button, LogoLoader, Modal, StatusTags, TableAdv } from "../../../../../components";
import { fetchAllAiCallsApi } from "../../../../../redux/slices/aicalls/aicallsApi";
import { usePagination } from "../../../../../hooks";
import { totalCountStart } from "../../../../../redux/slices/totalCount/totalCountSlice";
import { useDispatch, useSelector } from "react-redux";
import AiCallSummaryModel from "./AiCallSummaryModel";
import { Link } from "react-router-dom";
import { formatWords2 } from "../../../../../utils/stringUtils";

const aiCallStatus = {
  1: "Initiated",
  2: "Success",
  3: "Failed",
  4: "Skipped",
  5: "Scheduled",
  6: "Not Answered",
  7: "Intrested",
  8: "Not Intrested",
  9: "Cancel"
}

const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  const optionsDate = { year: "numeric", month: "long", day: "2-digit" };
  const formattedDate = date.toLocaleDateString("en-US", optionsDate);

  const optionsTime = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

  return `${formattedDate} ${formattedTime}`;
};

const AiCalls = ({jobPostId}) => {
  const dispatch = useDispatch();
  const { value } = useSelector((state) => state.totalCount);
  const totalCount = value?.jobpostaiclls?.data || 0
  const { page, limit, setPage, handleLimitChange } = usePagination(10, (newPage, newLimit) => {});

  const [aiCalls, setAiCalls] = useState([]);
  const [isModelOpen, setIsModelopen] = useState(false);
  const [selectedCall, setSelectedCall] = useState();
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      key: "model",
      label: "Model",
      columnMenu:false
    },
    {
      key: "applicantName",
      label: "Applicant Name",
      columnMenu:false,
      render: (row) => (
        <Link
          to={`/ats/applicants/applicant-details`}
          state={{ applicantId: row.applicantId, source: "kult-ai-calls", selectedJobUiid: jobPostId}}
          className="text-info-400 flex items-center gap-.5"
        >
          <span className="mx-2">{formatWords2(row.applicantName)}</span>
        </Link>
      ),
    },
    {
      key: "applicantNumber",
      label: "Applicant Number",
      columnMenu:false
    },
    {
      key: "callInitiatedTime",
      label: "Call Initiated Time",
      columnMenu:false
    },
    {
      key: "addedBy",
      label: "Call Initiated By",
      columnMenu:false
    },
    {
      key: "applicantLocation",
      label: "Applicant Location",
      columnMenu:false
    },
    {
      key: "status",
      label: "Status",
      columnMenu:false,
      render: (row) => (
        <div className="flex flex-column flex-wrap">
            <StatusTags tag={row?.status} />
            {row?.status === "Skipped" && (
                <p className="italic">{row?.skipReason}</p>
            )}
        </div>
      ),
    },
    {
      key: "view",
      label: "Actions",
      columnMenu:false,
      render: (row) => 
          <Button 
              type="icon"
              icon={"visibility"}
              variant="text"
              color="black"
              size="sm"
              onClick={() => {
                  setIsModelopen(true);
                  setSelectedCall(row);
              }}
          />
    }
  ];

  const rows = aiCalls?.map((call) => ({
    model: "Kult AI Call v1",
    applicantName: `${call?.candidateId?.firstName || ""} ${call?.candidateId?.lastName || ""}`,
    applicantNumber: `${call?.candidateId?.mobile?.primary?.code || ""} ${call?.candidateId?.mobile?.primary?.number || ""}`,
    callInitiatedTime: formatDateTime(call?.callinitiatedTime) || "",
    applicantLocation: `${call?.candidateId?.location?.primary?.code || ""} ${call?.candidateId?.mobile?.primary?.number || ""}`,
    status: aiCallStatus[call?.status],
    skipReason: call?.skipReason || "",
    callId: call?.callId,
    integrationId: call?.integrationId,
    addedBy: `${call?.addedBy?.firstName} ${call?.addedBy?.lastName}`,
    endedReason: call?.endedReason,
    uiid: call?.uiid,
    applicantId: call?.candidateId?.uiid,
    statusId: call?.status
  }));

  const mappedRows = mapTableRows(rows, columns);

  const fetchAiCalls = async () => {
    try {
      setLoading(true);
      const payload = {
          coll: "AiCall",
          fil: {jobPostId: {type: "NONE", value: jobPostId}},
          ref: "Candidate JobPost",
          incl: ["uiid firstName lastName mobile location currentStatus statusHistory","uiid jobId"],
          extret: "uiid firstName lastName"
      }
      const response = await fetchAllAiCallsApi(payload);
      if (response?.success) {
          setAiCalls(response?.data?.data);
      }
    } catch (error) {
        console.error("Error fetching AI Calls:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(
      totalCountStart({
        collection: "aicalls",
        coll: "AiCall",
        filters: {jobPostId: {type: "NONE", value: jobPostId}},
        tname: "jobpostaiclls",
        app: "ats",
      })
    );
  }, []);

  useEffect(() => {
    fetchAiCalls();
  }, [jobPostId, dispatch, page, limit]);

  return (
    <div>
      <TableAdv
        tableId={"jobs_jobdetails_aicalls_table"}
        rows={mappedRows}
        columns={columns}
        serverPagination={true}
        totalCount={totalCount}
        page={page}
        limit={limit}
        onPaginationChange={setPage}
        onLimitChange={handleLimitChange}
        toolbarRight={{
          fullScreen: true
        }}
      />

      <Modal
        isOpen={isModelOpen}
        title={"Ai Call Summary"}
        titleColor={"primary"}
        onClose={() => setIsModelopen(false)}
        width="max-w-5xl"
      >
        <AiCallSummaryModel call={selectedCall} callBack={() => fetchAiCalls()} />
      </Modal>

      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default AiCalls;